/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { AudioBufferState } from "constants/AudioBufferState";
import { BlockTypes } from "constants/BlockTypes";
import { ClipUploadStates } from "constants/ClipUploadStates";
import TranscriptionState from "constants/TranscriptionState";
import { LegacyBehanceUserData } from "types/Asset";
import { ClipId } from "types/Clip";
import { AdobeId } from "reducers/CurrentUserReducer";
import { AssetId } from "@phonos/types";

export type BlockId = string;

export interface BaseBlock {
  id: BlockId;
  type: BlockTypes;
  clipIds: ClipId[];
  uploadState: ClipUploadStates | null; // TODO: Safe to assume as `ClipUploadStates`?
  audioBufferState?: AudioBufferState;
  startTime: number;
  endTime: number;
  // TODO: Only defines block members needed for the drag/drop feature. Add the rest as needed.
}

export interface RecordingBlock extends BaseBlock {
  type: BlockTypes.RECORDING_SESSION;
  duration: number;
  transcriptionState: TranscriptionState | undefined;
}

export interface PlaceholderBlock extends BaseBlock {
  type: BlockTypes.PLACEHOLDER;
  clipIds: [ClipId];
  text: string | null;
}

export type WaveformUploadInfo = {
  uploadState: ClipUploadStates;
  name?: string;
};

export type WaveformBlock = BaseBlock &
  WaveformUploadInfo & {
    type: BlockTypes.WAVEFORM;
    uploadState: ClipUploadStates;
    audioBufferState: AudioBufferState;
    assetId: AssetId | null;
    offset: number;
    isBackground: boolean;
    fadeOut: boolean;
    fadeIn: boolean;
    transcriptionState: TranscriptionState | null;
    duration: number;
    name?: string;
  };

export const isWaveformBlockBusy = (block: WaveformBlock): boolean => {
  return (
    isWaveformBlockUploading(block) ||
    block.audioBufferState === AudioBufferState.PENDING
  );
};

export const isWaveformBlockUploading = (block: WaveformBlock): boolean => {
  return block.uploadState === ClipUploadStates.STARTED;
};

export interface BlockToken {
  text: string | null;
  id: string;
  assetId: string;
  transcriptSegmentIndex: number | null;
  segmentWordIndex: number | null;
  playlistStartTime: number;
  playlistEndTime: number;
}

export interface BlockParagraph {
  indexOffset: number;
  tokens: BlockToken[];
}

export const blockTokens = (block: TranscriptionBlock): BlockToken[] => {
  return block._flatTokens;
};

export type BlockTranscript = BlockParagraph[];

export interface TranscriptionBlock extends BaseBlock {
  type: BlockTypes.TRANSCRIPTION;
  transcript: BlockTranscript;
  assetIds: AssetId[];
  unidentifiedSpeakerNumber: number;
  adobeId?: AdobeId;
  displayName?: string;
  displayNameIsOverridden?: boolean;
  isAnonymous?: boolean;
  userData?: LegacyBehanceUserData;
  audioBufferState: AudioBufferState;
  _flatTokens: BlockToken[]; // private, use `blockTokens` instead. stored here for performance reasons
}

export const isTranscriptionBlock = (
  block: Block
): block is TranscriptionBlock => {
  return block.type === BlockTypes.TRANSCRIPTION;
};

export const isRecordingBlock = (block: Block): block is RecordingBlock => {
  return block.type === BlockTypes.RECORDING_SESSION;
};

export type Block =
  | RecordingBlock
  | PlaceholderBlock
  | WaveformBlock
  | TranscriptionBlock;
